import axios from "axios";
import { API } from "./Api";
import {
    fetchQuickOrderByRazorpayId,
    isCreatedCustomerOrder,
    unwrapOrderPayload,
} from "./lastOrder";

export const PENDING_PAYMENT_KEY = "quickOrderPendingPayment";
export const PAYMENT_CHECK_EVENT = "quickorder:payment-check";
export const PAYMENT_STATUS = {
    CHECKOUT_OPEN: "checkout_open",
    PAID: "paid",
};

const PENDING_TTL_MS = 45 * 60 * 1000;
const DEFAULT_POLL_ATTEMPTS = 8;
const DEFAULT_POLL_DELAY_MS = 1500;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

let finalizeQueue = Promise.resolve();

const enqueueFinalize = (task) => {
    const run = finalizeQueue.then(task, task);
    finalizeQueue = run.then(
        () => undefined,
        () => undefined,
    );
    return run;
};

const readRawPendingPayment = () => {
    try {
        const raw = localStorage.getItem(PENDING_PAYMENT_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (!parsed?.razorpayOrderId) return null;

        const savedAt = Number(parsed.savedAt || 0);
        if (savedAt && Date.now() - savedAt > PENDING_TTL_MS) {
            localStorage.removeItem(PENDING_PAYMENT_KEY);
            return null;
        }

        return parsed;
    } catch (error) {
        console.error("Invalid pending payment:", error);
        localStorage.removeItem(PENDING_PAYMENT_KEY);
        return null;
    }
};

export const getPendingPayment = () => readRawPendingPayment();

export const hasConfirmedPaymentAttempt = (session) =>
    Boolean(
        session?.status === PAYMENT_STATUS.PAID ||
            (session?.razorpayPaymentId && session?.razorpaySignature),
    );

export const savePendingPayment = (session = {}) => {
    if (!session?.razorpayOrderId) return null;

    const current = readRawPendingPayment();
    const next = {
        ...current,
        ...session,
        status:
            session.status ||
            (hasConfirmedPaymentAttempt({ ...current, ...session })
                ? PAYMENT_STATUS.PAID
                : current?.status || PAYMENT_STATUS.CHECKOUT_OPEN),
        savedAt: Date.now(),
    };

    localStorage.setItem(PENDING_PAYMENT_KEY, JSON.stringify(next));
    return next;
};

export const patchPendingPayment = (patch = {}) => {
    const current = readRawPendingPayment();

    if (!current && !patch.razorpayOrderId) {
        return null;
    }

    return savePendingPayment({
        ...(current || {}),
        ...patch,
    });
};

export const clearPendingPayment = (razorpayOrderId) => {
    const current = readRawPendingPayment();

    if (
        razorpayOrderId &&
        current?.razorpayOrderId &&
        current.razorpayOrderId !== razorpayOrderId
    ) {
        return;
    }

    localStorage.removeItem(PENDING_PAYMENT_KEY);
};

export const dispatchPaymentCheck = (detail = {}) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(PAYMENT_CHECK_EVENT, { detail }));
};

const CALLBACK_PARAM_KEYS = [
    "razorpay_payment_id",
    "razorpay_order_id",
    "razorpay_signature",
    "payment_return",
];

export const parseRazorpayCallbackParams = () => {
    if (typeof window === "undefined") {
        return {
            razorpayOrderId: null,
            razorpayPaymentId: null,
            razorpaySignature: null,
        };
    }

    const search = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(
        String(window.location.hash || "").replace(/^#/, "").replace(/^\?/, ""),
    );

    const read = (key) => search.get(key) || hash.get(key) || null;

    return {
        razorpayOrderId: read("razorpay_order_id"),
        razorpayPaymentId: read("razorpay_payment_id"),
        razorpaySignature: read("razorpay_signature"),
    };
};

export const stripRazorpayCallbackParams = () => {
    if (typeof window === "undefined") return;

    const url = new URL(window.location.href);
    CALLBACK_PARAM_KEYS.forEach((key) => url.searchParams.delete(key));

    window.history.replaceState(
        window.history.state,
        "",
        `${url.pathname}${url.search}${url.hash}`,
    );
};

const createWebBasedCustomerOrder = async ({
    razorpayOrderId,
    razorpayPaymentId,
    razorpaySignature,
    payload = {},
    phone = "",
}) => {
    const response = await axios.post(
        `${API.BASE_URL}order/create-web-based-customer-order`,
        {
            razorpayOrderId,
            razorpayPaymentId: razorpayPaymentId || "",
            razorpaySignature: razorpaySignature || "",
            arrivalTimeDurationInMinutes: payload.arrivalTimeDurationInMinutes,
            ordersType: payload.ordersType || "PICKUP",
            deliveryDetails: payload.deliveryDetails ?? null,
            outlet: payload.outlet,
            paymentMode: payload.paymentMode || "ONLINE",
            readyTime: payload.readyTime,
            pickupOption: payload.pickupOption,
            makingTimeDuration: payload.makingTimeDuration,
            customerPhoneNumber: phone || "",
        },
        {
            headers: {
                "Content-Type": "application/json",
            },
        },
    );

    return unwrapOrderPayload(response?.data?.data || response?.data);
};

const pollCreatedOrder = async (
    razorpayOrderId,
    attempts = DEFAULT_POLL_ATTEMPTS,
    delayMs = DEFAULT_POLL_DELAY_MS,
) => {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
        try {
            const order = await fetchQuickOrderByRazorpayId(razorpayOrderId);

            if (isCreatedCustomerOrder(order)) {
                return order;
            }
        } catch (error) {
            console.error(
                "get-razorpay-order-quick-order:",
                error?.response?.data || error,
            );
        }

        if (attempt < attempts - 1) {
            await wait(delayMs);
        }
    }

    return null;
};

export const finalizeQuickOrder = ({
    razorpayOrderId,
    razorpayPaymentId,
    razorpaySignature,
    payload = {},
    phone = "",
    pollAttempts = DEFAULT_POLL_ATTEMPTS,
    pollDelayMs = DEFAULT_POLL_DELAY_MS,
    allowCreateWithoutSignature = false,
} = {}) =>
    enqueueFinalize(async () => {
        if (!razorpayOrderId) return null;

        const canCreateWithSignature = Boolean(
            razorpayPaymentId && razorpaySignature,
        );
        const earlyPollAttempts = Math.min(
            canCreateWithSignature ? 3 : pollAttempts,
            pollAttempts,
        );
        let order = await pollCreatedOrder(
            razorpayOrderId,
            earlyPollAttempts,
            pollDelayMs,
        );

        if (order) return order;

        if (canCreateWithSignature) {
            try {
                const created = await createWebBasedCustomerOrder({
                    razorpayOrderId,
                    razorpayPaymentId,
                    razorpaySignature,
                    payload,
                    phone,
                });

                if (isCreatedCustomerOrder(created)) {
                    return created;
                }
            } catch (error) {
                console.error(
                    "create-web-based-customer-order:",
                    error?.response?.data || error,
                );
            }
        }

        const remainingAttempts = Math.max(pollAttempts - earlyPollAttempts, 0);
        if (remainingAttempts > 0) {
            order = await pollCreatedOrder(
                razorpayOrderId,
                remainingAttempts,
                pollDelayMs,
            );
            if (order) return order;
        }

        if (canCreateWithSignature || allowCreateWithoutSignature) {
            try {
                const created = await createWebBasedCustomerOrder({
                    razorpayOrderId,
                    razorpayPaymentId,
                    razorpaySignature,
                    payload,
                    phone,
                });

                if (isCreatedCustomerOrder(created)) {
                    return created;
                }
            } catch (error) {
                console.error(
                    "create-web-based-customer-order retry:",
                    error?.response?.data || error,
                );
            }

            return pollCreatedOrder(razorpayOrderId, 2, pollDelayMs);
        }

        return null;
    });

export const recoverPendingPayment = async ({
    pollAttempts = 15,
    pollDelayMs = 2000,
    abandonIfUnpaid = false,
} = {}) => {
    const callback = parseRazorpayCallbackParams();
    let session = readRawPendingPayment();

    if (callback.razorpayOrderId || callback.razorpayPaymentId) {
        session = savePendingPayment({
            ...(session || {}),
            razorpayOrderId:
                callback.razorpayOrderId || session?.razorpayOrderId,
            razorpayPaymentId:
                callback.razorpayPaymentId || session?.razorpayPaymentId,
            razorpaySignature:
                callback.razorpaySignature || session?.razorpaySignature,
            outletId: session?.outletId || localStorage.getItem("outletId"),
            status:
                callback.razorpayPaymentId && callback.razorpaySignature
                    ? PAYMENT_STATUS.PAID
                    : session?.status || PAYMENT_STATUS.CHECKOUT_OPEN,
        });
        stripRazorpayCallbackParams();
    }

    if (!session?.razorpayOrderId) {
        return { status: "none" };
    }

    const confirmed = hasConfirmedPaymentAttempt(session);

    if (abandonIfUnpaid && !confirmed) {
        const order = await pollCreatedOrder(session.razorpayOrderId, 2, 600);

        if (order) {
            return { status: "created", order, session };
        }

        clearPendingPayment(session.razorpayOrderId);
        return { status: "abandoned", session };
    }

    const order = await finalizeQuickOrder({
        razorpayOrderId: session.razorpayOrderId,
        razorpayPaymentId: session.razorpayPaymentId,
        razorpaySignature: session.razorpaySignature,
        payload: session.payload || {},
        phone: session.phone || "",
        pollAttempts: confirmed ? pollAttempts : Math.min(pollAttempts, 6),
        pollDelayMs: confirmed ? pollDelayMs : 1000,
        allowCreateWithoutSignature: false,
    });

    if (order) {
        return { status: "created", order, session };
    }

    return { status: "pending", session };
};
